import { useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Truck, PackageSearch, Warehouse, Plane, Container, MapPinned, ShieldCheck,
  ArrowRight, ArrowUpRight, Phone, Check, ChevronRight,
} from "lucide-react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { RevealText, CustomCursor, Magnetic } from "./scroll";
import { HighwayDivider, GearIn } from "./transport";
import { SmoothScroll } from "./experience";
import { SERVICES, CONTACT, serviceBySlug, SERVICE_SEO, SERVICE_FAQ, breadcrumb, faqSchema } from "./marketingData";

const ICONS = { Truck, PackageSearch, Warehouse, Plane, Container, MapPinned, ShieldCheck };
const btnMotion = { whileHover: { scale: 1.04 }, whileTap: { scale: 0.97 } };

export default function ServicePage() {
  const { slug } = useParams();
  const service = serviceBySlug(slug);
  const heroRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const { scrollYProgress } = useScroll();
  const progressX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "25%"]);
  const heroScale = useTransform(heroProgress, [0, 1], [1, 1.14]);
  const heroFade = useTransform(heroProgress, [0, 0.85], [1, 0]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = ICONS[service.icon] || Truck;
  const others = SERVICES.filter((s) => s.slug !== slug);
  const seo = SERVICE_SEO[slug] || { title: `${service.title} — ITO`, description: service.short };
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: service.title,
    description: seo.description,
    areaServed: "IN",
    provider: {
      "@type": "MovingCompany",
      name: "Indian Transport Organisation",
      telephone: "+91-8492894602",
      url: "https://itoworldwide.com/",
      address: {
        "@type": "PostalAddress",
        streetAddress: "313, Mohid Heights, RTO Lane, Andheri West",
        addressLocality: "Mumbai",
        addressRegion: "MH",
        postalCode: "400053",
        addressCountry: "IN",
      },
    },
  };

  const faqs = SERVICE_FAQ[slug] || [];
  const jsonLd = [
    serviceJsonLd,
    breadcrumb([
      { name: "Home", path: "/" },
      { name: "Services", path: "/#services" },
      { name: service.title, path: `/services/${slug}` },
    ]),
  ];
  if (faqs.length) jsonLd.push(faqSchema(faqs));

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden md:[cursor:none]">
      <CustomCursor />
      <SmoothScroll />
      <Seo title={seo.title} description={seo.description} path={`/services/${slug}`} image={service.image} imageAlt={service.title} jsonLd={jsonLd} />
      <motion.div style={{ scaleX: progressX }} className="fixed top-0 left-0 right-0 h-1 bg-[#C84B31] origin-left z-[60]" />
      <MarketingNav heroDark />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[70vh] flex items-end pb-14 pt-32 overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A18]/90 via-[#1A1A18]/60 to-[#1A1A18]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/70 to-transparent" />
        </motion.div>
        <motion.div style={{ opacity: heroFade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-xs text-white/80 mb-5"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <a href="/#services" className="hover:text-white">Services</a>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#D4A373]">{service.tag}</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white mb-5"
          >
            <Icon className="w-4 h-4" /> {service.tag}
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]"
          >
            {service.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-5 text-lg text-white/85 max-w-2xl leading-relaxed"
          >
            {service.hero}
          </motion.p>
        </motion.div>
      </section>

      {/* INTRO + FEATURES */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Overview</p>
              <p className="text-xl md:text-2xl font-heading font-medium leading-snug text-[#1A1A18]">
                {service.intro}
              </p>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-2 gap-8">
              {service.features.map((f, i) => (
                <Reveal key={f.h} delay={(i % 2) * 0.08}>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 w-7 h-7 rounded-full bg-[#C84B31]/10 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-[#C84B31]" />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-base mb-1">{f.h}</h3>
                      <p className="text-sm text-[#4F4C44] leading-relaxed">{f.p}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1} className="sticky top-24 rounded-2xl bg-white border border-[#1A1A18]/10 p-8 shadow-sm">
              <div className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-4">Typical cargo</div>
              <ul className="space-y-3 mb-8">
                {service.cargo.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-sm text-[#1A1A18]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C84B31]" /> {c}
                  </li>
                ))}
              </ul>
              <div className="rounded-xl bg-[#1A1A18] text-white p-6">
                <div className="font-heading font-bold text-lg mb-2">Need a quote?</div>
                <p className="text-sm text-white/85 mb-5">Talk to our team about your {service.title.toLowerCase()} movement.</p>
                <Magnetic className="w-full">
                  <motion.a {...btnMotion} href={CONTACT.phoneHref} data-testid="service-quote-btn" data-cursor
                    className="flex items-center justify-center gap-2 rounded-full bg-[#C84B31] px-5 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                    <Phone className="w-4 h-4" /> Call {CONTACT.phone}
                  </motion.a>
                </Magnetic>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <HighwayDivider />

      <FaqSection faqs={faqs} title={`${service.title} — frequently asked questions`} testid="service-faq" />

      {slug === "project-odc-cargo" && (
        <section className="py-12 bg-[#F9F8F6] border-t border-[#1A1A18]/10" data-testid="service-resources">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 rounded-2xl border border-[#C84B31]/25 bg-[#C84B31]/[0.04] p-6">
            <h2 className="font-heading font-bold text-xl mb-3">ODC &amp; heavy-haul guides and city coverage</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/guides/odc-heavy-haul-transport-india" data-testid="service-guide-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#C84B31] hover:underline">
                Read the ODC &amp; heavy-haul guide <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/odc-heavy-haul-transporter" data-testid="service-city-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#C84B31] hover:underline">
                ODC transporter by city <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* OTHER SERVICES */}
      <section className="py-20 bg-white border-t border-[#1A1A18]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-10 flex items-end justify-between">
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-2xl md:text-3xl" text="Other services" />
            <Link to="/#services" className="text-sm font-semibold text-[#C84B31] inline-flex items-center gap-1.5">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.slice(0, 3).map((s, i) => {
              const OIcon = ICONS[s.icon] || Truck;
              return (
                <GearIn key={s.slug} delay={i * 0.08}>
                  <Link to={`/services/${s.slug}`} data-testid={`other-service-${s.slug}`} data-cursor
                    className="group block rounded-2xl border border-[#1A1A18]/10 overflow-hidden hover:border-[#C84B31]/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-40 overflow-hidden">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/50 to-transparent" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2.5 mb-2">
                        <OIcon className="w-4 h-4 text-[#C84B31]" />
                        <h3 className="font-heading font-semibold text-lg">{s.title}</h3>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                        Learn more <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </GearIn>
              );
            })}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
